import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import {
  qk,
  useClient,
  useCrawlMutations,
  useCrawlRun,
  usePages,
  useReviewSummary,
} from '@/api/queries'
import { ApiError } from '@/api/errors'
import { PageHeader } from '@/components/app/PageHeader'
import { ConfirmDialog } from '@/components/ui/Overlay'
import { useToast } from '@/components/ui/Toast'
import { CrawlSummaryBand } from './CrawlSummaryBand'
import { ReviewQueueBand } from './ReviewQueueBand'
import { PageInventoryTable } from './PageInventoryTable'
import { CrawlErrorsBand } from './CrawlErrorsBand'
import { CRAWL_POLL_MS } from '@/lib/constants'
import type { CrawledPage } from '@/types'

/**
 * Default landing page for a client. Four unrelated bands, so this file is
 * composition plus the two confirm dialogs (docs/06-component-refactor.md §5.6).
 */
export function CrawlPage() {
  const { clientId } = useParams()
  const { data: client } = useClient(clientId!)
  const { data: run, isLoading: runLoading } = useCrawlRun(clientId!)
  const { data: pages = [], isLoading: pagesLoading } = usePages(clientId!)
  const { data: summary } = useReviewSummary(clientId!)
  const crawl = useCrawlMutations(clientId!)
  const qc = useQueryClient()
  const toast = useToast()

  const [confirmRecrawl, setConfirmRecrawl] = useState(false)
  const [confirmExclude, setConfirmExclude] = useState<CrawledPage | null>(null)

  const running = run?.status === 'running'

  /**
   * Poll only while a run is live *and* the tab is focused. Polling a
   * finished crawl, or one nobody is watching, is free load on the API for no
   * information (docs/02-app-shell.md §6).
   */
  useEffect(() => {
    if (!running) return
    const tick = () => {
      if (document.hidden) return
      void qc.invalidateQueries({ queryKey: qk.crawlRun(clientId!) })
      void qc.invalidateQueries({ queryKey: qk.reviewSummary(clientId!) })
    }
    const id = window.setInterval(tick, CRAWL_POLL_MS)
    return () => window.clearInterval(id)
  }, [running, clientId, qc])

  const errors = pages.filter((p) => p.error !== null)

  return (
    <>
      <PageHeader
        title="Crawl dashboard"
        description="What the last run reached, and what is still waiting on a human."
      />

      <div className="space-y-5">
        <CrawlSummaryBand
          run={run}
          loading={runLoading}
          onRecrawl={() => setConfirmRecrawl(true)}
        />

        <ReviewQueueBand summary={summary} clientId={clientId!} />

        <PageInventoryTable
          pages={pages}
          loading={pagesLoading}
          onRecrawl={(page) =>
            crawl.retry.mutate(page.id, { onSuccess: () => toast.push('Page queued for re-crawl.') })
          }
          onExclude={setConfirmExclude}
          onImportance={(pageId, importance) =>
            crawl.setImportance.mutate({ pageId, importance })
          }
        />

        <CrawlErrorsBand
          errors={errors}
          busy={crawl.retry.isPending}
          onRetry={(page) =>
            crawl.retry.mutate(page.id, { onSuccess: () => toast.push('Retrying that page.') })
          }
          onRetryAll={() => {
            errors.forEach((page) => crawl.retry.mutate(page.id))
            toast.push(`Retrying ${errors.length} pages.`)
          }}
        />
      </div>

      <ConfirmDialog
        open={confirmRecrawl}
        onClose={() => setConfirmRecrawl(false)}
        onConfirm={async () => {
          try {
            await crawl.start.mutateAsync()
            toast.push('Crawl started.')
          } catch (cause) {
            // 422 without a page limit, 409 while a run is unfinished (APP-54).
            toast.push(cause instanceof ApiError ? cause.message : 'The crawl did not start.', 'error')
          }
          setConfirmRecrawl(false)
        }}
        title={`Re-crawl ${client?.name ?? 'this client'}?`}
        description={`This re-crawls ${client?.primaryDomain ?? 'the configured domains'} using the current crawl configuration. Content that changed since the last run re-enters review as pending; existing verdicts on unchanged content are kept.`}
        confirmLabel="Start crawl"
        busy={crawl.start.isPending}
      />

      <ConfirmDialog
        open={Boolean(confirmExclude)}
        onClose={() => setConfirmExclude(null)}
        onConfirm={async () => {
          if (!confirmExclude) return
          await crawl.exclude.mutateAsync(confirmExclude.id)
          setConfirmExclude(null)
          toast.push('Page excluded from future extraction.')
        }}
        title={`Exclude ${confirmExclude ? new URL(confirmExclude.url).pathname : ''}?`}
        description="This writes a URL exclusion and drops the page from future extraction. It does not delete entities already extracted from it — remove those on their own review surface if you want them gone."
        confirmLabel="Exclude page"
        destructive
        busy={crawl.exclude.isPending}
      />
    </>
  )
}
